import React, { useState, useEffect } from 'react';
import { FiBell, FiGlobe, FiMoon, FiSun, FiSave, FiRefreshCw, FiCheckCircle } from 'react-icons/fi';

export const PreferencesSection = ({ 
  preferences = { darkMode: true }, 
  onPreferencesChange,
  onSavePreferences 
}) => {
  const [saving, setSaving] = useState(false);
  const [localPreferences, setLocalPreferences] = useState(preferences);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const handleChange = (key, value) => {
    const updated = {
      ...localPreferences,
      [key]: value
    };
    setLocalPreferences(updated);
    if (onPreferencesChange) {
      onPreferencesChange(updated);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSavePreferences(localPreferences);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-800/50 border border-amber-500/20 rounded-2xl shadow-lg overflow-hidden backdrop-blur-sm">
        <div className="px-6 py-5 border-b border-amber-500/10">
          <h2 className="text-xl font-bold text-white">
            Préférences
          </h2>
          <p className="mt-1 text-sm text-gray-400">
            Personnalisez votre expérience sur la plateforme
          </p>
        </div>
        <form onSubmit={handleSubmit} className="divide-y divide-amber-500/10">
          <div className="px-6 py-6">
            <div className="space-y-8">
              {/* Language Preference */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-gray-800/30 rounded-xl border border-amber-500/10">
                <div className="flex-1">
                  <h4 className="text-base font-medium text-white flex items-center">
                    <FiGlobe className="mr-2 h-5 w-5 text-amber-400" />
                    Langue
                  </h4>
                  <p className="text-sm text-gray-400 mt-1">
                    Choisissez votre langue préférée
                  </p>
                </div>
                <div className="w-full md:w-48">
                  <select
                    value={localPreferences.language || 'fr'}
                    onChange={(e) => handleChange('language', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700/50 border border-amber-500/30 rounded-lg text-white text-sm focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-colors"
                  >
                    <option value="fr" className="bg-gray-800">Français</option>
                    <option value="en" className="bg-gray-800">English</option>
                  </select>
                </div>
              </div>

              {/* Theme Preference */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-gray-800/30 rounded-xl border border-amber-500/10">
                <div className="flex-1">
                  <h4 className="text-base font-medium text-white flex items-center">
                    {localPreferences.darkMode ? (
                      <FiMoon className="mr-2 h-5 w-5 text-amber-400" />
                    ) : (
                      <FiSun className="mr-2 h-5 w-5 text-amber-400" />
                    )}
                    Thème
                  </h4>
                  <p className="text-sm text-gray-400 mt-1">
                    Choisissez entre le mode clair et le mode sombre
                  </p>
                </div>
                <div className="flex items-center">
                  <span className="mr-3 text-sm font-medium text-gray-300">
                    {localPreferences.darkMode ? 'Sombre' : 'Clair'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleChange('darkMode', !localPreferences.darkMode)}
                    className={`${
                      localPreferences.darkMode ? 'bg-amber-600' : 'bg-gray-700'
                    } relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-gray-900`}
                    role="switch"
                    aria-checked={localPreferences.darkMode}
                  >
                    <span className="sr-only">Basculer le thème</span>
                    <span
                      aria-hidden="true"
                      className={`${
                        localPreferences.darkMode ? 'translate-x-7' : 'translate-x-0.5'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </button>
                </div>
              </div>

              {/* Notifications */}
              <div className="p-4 bg-gray-800/30 rounded-xl border border-amber-500/10">
                <h4 className="text-base font-medium text-white flex items-center">
                  <FiBell className="mr-2 h-5 w-5 text-amber-400" />
                  Notifications
                </h4>
                <p className="text-sm text-gray-400 mt-1 mb-4">
                  Contrôlez comment vous recevez les notifications
                </p>
                
                <div className="space-y-5">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors">
                    <div>
                      <label htmlFor="email-notifications" className="block text-sm font-medium text-white">
                        Notifications par email
                      </label>
                      <p className="text-xs text-gray-400">
                        Recevoir des mises à jour par email
                      </p>
                    </div>
                    <div className="flex items-center">
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          id="email-notifications"
                          name="email-notifications"
                          type="checkbox"
                          checked={localPreferences.emailNotifications || false}
                          onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                          className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer transition-transform duration-200 ease-in-out"
                        />
                        <label
                          htmlFor="email-notifications"
                          className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${
                            localPreferences.emailNotifications ? 'bg-amber-600' : 'bg-gray-600'
                          }`}
                        ></label>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors">
                    <div>
                      <label htmlFor="push-notifications" className="block text-sm font-medium text-white">
                        Notifications push
                      </label>
                      <p className="text-xs text-gray-400">
                        Recevoir des notifications sur cet appareil
                      </p>
                    </div>
                    <div className="flex items-center">
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          id="push-notifications"
                          name="push-notifications"
                          type="checkbox"
                          checked={localPreferences.pushNotifications || false}
                          onChange={(e) => handleChange('pushNotifications', e.target.checked)}
                          className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer transition-transform duration-200 ease-in-out"
                        />
                        <label
                          htmlFor="push-notifications"
                          className={`toggle-checkbox-label block overflow-hidden h-5 rounded-full cursor-pointer ${
                            localPreferences.pushNotifications ? 'bg-amber-600' : 'bg-gray-600'
                          }`}
                        ></label>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors">
                    <div>
                      <label htmlFor="marketing-emails" className="block text-sm font-medium text-white">
                        Communications marketing
                      </label>
                      <p className="text-xs text-gray-400">
                        Recevoir des offres et des mises à jour
                      </p>
                    </div>
                    <div className="flex items-center">
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          id="marketing-emails"
                          name="marketing-emails"
                          type="checkbox"
                          checked={localPreferences.marketingEmails || false}
                          onChange={(e) => handleChange('marketingEmails', e.target.checked)}
                          className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer transition-transform duration-200 ease-in-out"
                        />
                        <label
                          htmlFor="marketing-emails"
                          className={`toggle-checkbox-label block overflow-hidden h-5 rounded-full cursor-pointer ${
                            localPreferences.marketingEmails ? 'bg-amber-600' : 'bg-gray-600'
                          }`}
                        ></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-800/30 border-t border-amber-500/10 text-right">
            {showSuccess && (
              <div className="inline-flex items-center px-4 py-2 text-sm text-green-400 bg-green-900/30 border border-green-800/50 rounded-lg mb-4">
                <FiCheckCircle className="mr-2 h-4 w-4" />
                Préférences enregistrées avec succès
              </div>
            )}
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-600/90 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <FiRefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <FiSave className="-ml-1 mr-2 h-4 w-4" />
                  Enregistrer les préférences
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add custom styles for the toggle switches
const toggleStyles = `
  .toggle-checkbox:checked {
    right: 0;
    border-color: #68D391;
  }
  .toggle-checkbox:checked + .toggle-label {
    background-color: #68D391;
  }
  .toggle-checkbox:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(236, 201, 75, 0.5);
  }
  .toggle-checkbox {
    transition: all 0.3s ease-in-out;
  }
  .toggle-label {
    transition: background-color 0.3s ease-in-out;
  }
`;

// Add styles to the document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = toggleStyles;
  document.head.appendChild(styleElement);
}

export default PreferencesSection;
